/**
 * iCure Data Stack API Documentation
 * The iCure Data Stack Application API is the native interface to iCure. This version is obsolete, please use v2.
 *
 * OpenAPI spec version: v1
 *
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually.
 */
import { XHR } from './XHR'
import { AbstractFilterPatient } from '../model/AbstractFilterPatient'
import { Content } from '../model/Content'
import { DataOwnerRegistrationSuccess } from '../model/DataOwnerRegistrationSuccess'
import { Delegation } from '../model/Delegation'
import { DocIdentifier } from '../model/DocIdentifier'
import { FilterChainPatient } from '../model/FilterChainPatient'
import { IdWithRev } from '../model/IdWithRev'
import { ListOfIds } from '../model/ListOfIds'
import { PaginatedListPatient } from '../model/PaginatedListPatient'
import { PaginatedListString } from '../model/PaginatedListString'
import { Patient } from '../model/Patient'
import { AuthenticationProvider, NoAuthenticationProvider } from '../../icc-x-api/auth/AuthenticationProvider'
import { iccRestApiPath } from './IccRestApiPath'
import { EntityBulkShareResult } from '../model/requests/EntityBulkShareResult'
import { BulkShareOrUpdateMetadataParams } from '../model/requests/BulkShareOrUpdateMetadataParams'
import { TimingInfo } from '../model/TimingInfo'
import { ConflictResolutionRequest } from '../model/ConflictResolutionRequest'
import { ConflictResolutionResult } from '../model/ConflictResolutionResult'
import { ConflictResolutionStrategy } from '../model/ConflictResolutionStrategy'
import { MergeResult } from '../model/MergeResult'

export class IccPatientApi {
  host: string
  _headers: Array<XHR.Header>
  authenticationProvider: AuthenticationProvider
  fetchImpl?: (input: RequestInfo, init?: RequestInit) => Promise<Response>

  get headers(): Promise<Array<XHR.Header>> {
    return Promise.resolve(this._headers)
  }

  constructor(
    host: string,
    headers: any,
    authenticationProvider?: AuthenticationProvider,
    fetchImpl?: (input: RequestInfo, init?: RequestInit) => Promise<Response>
  ) {
    this.host = iccRestApiPath(host)
    this._headers = Object.keys(headers).map((k) => new XHR.Header(k, headers[k]))
    this.authenticationProvider = !!authenticationProvider ? authenticationProvider : new NoAuthenticationProvider()
    this.fetchImpl = fetchImpl
  }
  setHeaders(h: Array<XHR.Header>) {
    this._headers = h
  }

  handleError(e: XHR.XHRError): never {
    throw e
  }

  /**
   * Returns the id and _rev of created patients
   * @summary Create patients in bulk
   * @param body
   */
  async bulkCreatePatients(body?: Array<Patient>): Promise<Array<IdWithRev>> {
    let _body = null
    _body = body

    const _url = this.host + `/patient/batch` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('POST', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((it) => new IdWithRev(it)))
      .catch((err) => this.handleError(err))
  }

  /**
   * Returns the id and _rev of modified patients
   * @summary Modify patients in bulk
   * @param body
   */
  async bulkUpdatePatients(body?: Array<Patient>): Promise<Array<IdWithRev>> {
    let _body = null
    _body = body

    const _url = this.host + `/patient/batch` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('PUT', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((it) => new IdWithRev(it)))
      .catch((err) => this.handleError(err))
  }

  /**
   * Returns the count of patients shared directly with the provided hcp (ignoring hierarchies).
   * WARNING: the current implementation can count some patients twice if they have both security metadata and legacy
   * delegations from before v8.
   */
  async countOfPatients(hcPartyId: string): Promise<Content> {
    let _body = null

    const _url = this.host + `/patient/hcParty/${encodeURIComponent(String(hcPartyId))}/count` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new Content(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * Name, last name, date of birth, and gender are required. After creation of the patient and obtaining the ID, you need to create an initial delegation.
   * @summary Create a patient
   * @param body
   */
  async createPatient(body?: Patient): Promise<Patient> {
    const _url = this.host + `/patient` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('POST', _url, headers, body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new Patient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * @summary Deletes a batch of patients.
   *
   * @param patientIds a ListOfIds containing the ids of the patients to delete.
   * @return a Promise that will resolve in an array of DocIdentifiers of the successfully deleted patients.
   */
  async deletePatients(patientIds: ListOfIds): Promise<Array<DocIdentifier>> {
    const headers = (await this.headers).filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand(
      'POST',
      this.host + `/patient/delete/batch` + '?ts=' + new Date().getTime(),
      headers,
      patientIds,
      this.fetchImpl,
      undefined,
      this.authenticationProvider.getAuthService()
    )
      .then((doc) => (doc.body as Array<JSON>).map((it) => new DocIdentifier(it)))
      .catch((err) => this.handleError(err))
  }

  /**
   * @summary Deletes a single patient by id.
   *
   * @param patientId the id of the patient to delete.
   * @return a Promise that will resolve in the DocIdentifier of the deleted patient.
   */
  async deletePatient(patientId: string): Promise<DocIdentifier> {
    return XHR.sendCommand(
      'DELETE',
      this.host + `/patient/${encodeURIComponent(patientId)}` + '?ts=' + new Date().getTime(),
      await this.headers,
      null,
      this.fetchImpl,
      undefined,
      this.authenticationProvider.getAuthService()
    )
      .then((doc) => new DocIdentifier(doc.body))
      .catch((err) => this.handleError(err))
  }

  /**
   * Returns a list of patients along with next start keys and Document ID. If the nextStartKey is Null it means that this is the last page.
   * @summary Filter patients for the current user (HcParty)
   * @param body
   * @param startKey The start key for pagination, depends on the filters used
   * @param startDocumentId A patient document ID
   * @param limit Number of rows
   * @param skip Skip rows
   * @param sort Sort key
   * @param desc Descending
   * @param collectTiming if true, include server-side filter timing information in the response
   */
  filterPatientsBy(
    startKey?: string,
    startDocumentId?: string,
    limit?: number,
    skip?: number,
    sort?: string,
    desc?: boolean,
    body?: FilterChainPatient,
    collectTiming?: false
  ): Promise<PaginatedListPatient>
  filterPatientsBy(
    startKey?: string,
    startDocumentId?: string,
    limit?: number,
    skip?: number,
    sort?: string,
    desc?: boolean,
    body?: FilterChainPatient,
    collectTiming?: true
  ): Promise<PaginatedListPatient & TimingInfo>
  async filterPatientsBy(
    startKey?: string,
    startDocumentId?: string,
    limit?: number,
    skip?: number,
    sort?: string,
    desc?: boolean,
    body?: FilterChainPatient,
    collectTiming: boolean = false
  ): Promise<PaginatedListPatient> {
    let _body = null
    _body = body

    const _url =
      this.host +
      `/patient/filter` +
      '?ts=' +
      new Date().getTime() +
      (startKey ? '&startKey=' + encodeURIComponent(String(startKey)) : '') +
      (startDocumentId ? '&startDocumentId=' + encodeURIComponent(String(startDocumentId)) : '') +
      (limit ? '&limit=' + encodeURIComponent(String(limit)) : '') +
      (skip ? '&skip=' + encodeURIComponent(String(skip)) : '') +
      (sort ? '&sort=' + encodeURIComponent(String(sort)) : '') +
      (desc ? '&desc=' + encodeURIComponent(String(desc)) : '')
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand(
      'POST',
      _url,
      headers,
      _body,
      this.fetchImpl,
      undefined,
      this.authenticationProvider.getAuthService(),
      undefined,
      false,
      collectTiming ? ['x-filter-timing-*'] : []
    )
      .then((doc) => Object.assign(new PaginatedListPatient(doc.body as JSON), collectTiming ? { responseHeaders: doc.responseHeaders } : {}))
      .catch((err) => this.handleError(err))
  }

  /**
   *
   * @summary Get Paginated List of Patients sorted by Access logs descending
   * @param userId A User ID
   * @param accessType The type of access (COMPUTER or USER)
   * @param startDate The start search epoch
   * @param startKey The start key for pagination
   * @param startDocumentId A patient document ID
   * @param limit Number of rows
   */
  async findByAccessLogUserAfterDate(
    userId: string,
    accessType?: string,
    startDate?: number,
    startKey?: string,
    startDocumentId?: string,
    limit?: number
  ): Promise<PaginatedListPatient> {
    let _body = null

    const _url =
      this.host +
      `/patient/byAccess/${encodeURIComponent(String(userId))}` +
      '?ts=' +
      new Date().getTime() +
      (accessType ? '&accessType=' + encodeURIComponent(String(accessType)) : '') +
      (startDate ? '&startDate=' + encodeURIComponent(String(startDate)) : '') +
      (startKey ? '&startKey=' + encodeURIComponent(String(startKey)) : '') +
      (startDocumentId ? '&startDocumentId=' + encodeURIComponent(String(startDocumentId)) : '') +
      (limit ? '&limit=' + encodeURIComponent(String(limit)) : '')
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new PaginatedListPatient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   *
   * @summary Get the patient having the provided externalId
   * @param externalId A external ID
   */
  async findByExternalId(externalId: string): Promise<Patient> {
    let _body = null

    const _url = this.host + `/patient/byExternalId/${encodeURIComponent(String(externalId))}` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new Patient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * Returns a list of patients along with next start keys and Document ID. If the nextStartKey is Null it means that this is the last page.
   * @summary Find patients for the current user (HcParty)
   * @param healthcarePartyId HealthcareParty Id. If not set, will use user&#x27;s hcpId
   * @param filterValue Optional value for filtering results
   * @param startKey The start key for pagination: a JSON representation of an array containing all the necessary components to form the Complex Key&#x27;s startKey
   * @param startDocumentId A patient document ID
   * @param limit Number of rows
   * @param sortDirection Optional value for providing a sorting direction (&#x27;asc&#x27;, &#x27;desc&#x27;). Set to &#x27;asc&#x27; by default.
   */
  async findByNameBirthSsinAuto(
    healthcarePartyId?: string,
    filterValue?: string,
    startKey?: string,
    startDocumentId?: string,
    limit?: number,
    sortDirection?: string
  ): Promise<PaginatedListPatient> {
    let _body = null

    const _url =
      this.host +
      `/patient/byNameBirthSsinAuto` +
      '?ts=' +
      new Date().getTime() +
      (healthcarePartyId ? '&healthcarePartyId=' + encodeURIComponent(String(healthcarePartyId)) : '') +
      (filterValue ? '&filterValue=' + encodeURIComponent(String(filterValue)) : '') +
      (startKey ? '&startKey=' + encodeURIComponent(String(startKey)) : '') +
      (startDocumentId ? '&startDocumentId=' + encodeURIComponent(String(startDocumentId)) : '') +
      (limit ? '&limit=' + encodeURIComponent(String(limit)) : '') +
      (sortDirection ? '&sortDirection=' + encodeURIComponent(String(sortDirection)) : '')
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new PaginatedListPatient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   *
   * @summary Provides a paginated list of patients with duplicate name for an hecparty
   * @param hcPartyId Healthcare party id
   * @param startKey The start key for pagination, depends on the filters used
   * @param startDocumentId A patient document ID
   * @param limit Number of rows
   */
  async findDuplicatesByName(hcPartyId: string, startKey?: string, startDocumentId?: string, limit?: number): Promise<PaginatedListPatient> {
    let _body = null

    const _url =
      this.host +
      `/patient/duplicates/name` +
      '?ts=' +
      new Date().getTime() +
      (hcPartyId ? '&hcPartyId=' + encodeURIComponent(String(hcPartyId)) : '') +
      (startKey ? '&startKey=' + encodeURIComponent(String(startKey)) : '') +
      (startDocumentId ? '&startDocumentId=' + encodeURIComponent(String(startDocumentId)) : '') +
      (limit ? '&limit=' + encodeURIComponent(String(limit)) : '')
    let headers = await this.headers
    return XHR.sendCommand('POST', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new PaginatedListPatient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   *
   * @summary Provides a paginated list of patients with duplicate ssin for an hecparty
   * @param hcPartyId Healthcare party id
   * @param startKey The start key for pagination, depends on the filters used
   * @param startDocumentId A patient document ID
   * @param limit Number of rows
   */
  async findDuplicatesBySsin(hcPartyId: string, startKey?: string, startDocumentId?: string, limit?: number): Promise<PaginatedListPatient> {
    let _body = null

    const _url =
      this.host +
      `/patient/duplicates/ssin` +
      '?ts=' +
      new Date().getTime() +
      (hcPartyId ? '&hcPartyId=' + encodeURIComponent(String(hcPartyId)) : '') +
      (startKey ? '&startKey=' + encodeURIComponent(String(startKey)) : '') +
      (startDocumentId ? '&startDocumentId=' + encodeURIComponent(String(startDocumentId)) : '') +
      (limit ? '&limit=' + encodeURIComponent(String(limit)) : '')
    let headers = await this.headers
    return XHR.sendCommand('POST', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new PaginatedListPatient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * Returns a list of patients
   * @summary Filter patients for the current user (HcParty)
   * @param firstName The first name
   * @param lastName The last name
   * @param dateOfBirth The date of birth
   */
  async fuzzySearch(firstName?: string, lastName?: string, dateOfBirth?: number): Promise<Array<Patient>> {
    let _body = null

    const _url =
      this.host +
      `/patient/fuzzy` +
      '?ts=' +
      new Date().getTime() +
      (firstName ? '&firstName=' + encodeURIComponent(String(firstName)) : '') +
      (lastName ? '&lastName=' + encodeURIComponent(String(lastName)) : '') +
      (dateOfBirth ? '&dateOfBirth=' + encodeURIComponent(String(dateOfBirth)) : '')
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((it) => new Patient(it)))
      .catch((err) => this.handleError(err))
  }

  /**
   * It gets patient administrative data.
   * @summary Get patient
   * @param patientId
   */
  async getPatient(patientId: string): Promise<Patient> {
    let _body = null

    const _url = this.host + `/patient/${encodeURIComponent(String(patientId))}` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new Patient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * (key, value) of the map is as follows: (ID of the owner of the encrypted AES key, encrypted AES keys)
   * @summary Get the HcParty encrypted AES keys indexed by owner.
   * @param patientId
   */
  async getPatientAesExchangeKeysForDelegate(
    patientId: string
  ): Promise<{ [delegatorId: string]: { [delegatorPubKeyFingerprint: string]: { [delegatePubKeyFingerprint: string]: string } } }> {
    let _body = null

    const _url = this.host + `/patient/${encodeURIComponent(String(patientId))}/aesExchangeKeys` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => JSON.parse(JSON.stringify(doc.body)))
      .catch((err) => this.handleError(err))
  }

  /**
   * It gets patient administrative data based on the identifier (root & extension) parameters.
   * @summary Get patient by identifier
   * @param hcPartyId
   * @param id
   * @param system
   */
  async getPatientByHealthcarepartyAndIdentifier(hcPartyId: string, id: string, system?: string): Promise<Patient> {
    let _body = null

    const _url =
      this.host +
      `/patient/${encodeURIComponent(String(hcPartyId))}/${encodeURIComponent(String(id))}` +
      '?ts=' +
      new Date().getTime() +
      (system ? '&system=' + encodeURIComponent(String(system)) : '')
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new Patient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * This endpoint is used to recover all keys that have already been created and that can be used to share information with this patient. It returns a map with the following structure: ID of the owner of the encrypted AES key -> encrypted AES key. The returned encrypted AES keys will have to be decrypted using the patient's private key.                  {                     \"hcparty 1 delegator ID\": \"AES hcparty key (encrypted using patient public RSA key)\"                     \"hcparty 2 delegator ID\": \"other AES hcparty key (encrypted using patient public RSA key)\"                 }
   * @summary Get the patient (identified by patientId) hcparty keys. Those keys are AES keys (encrypted) used to share information between HCPs and a patient.
   * @param patientId The patient Id for which information is shared
   */
  async getPatientHcPartyKeysForDelegate(patientId: string): Promise<{ [key: string]: string }> {
    let _body = null

    const _url = this.host + `/patient/${encodeURIComponent(String(patientId))}/keys` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => JSON.parse(JSON.stringify(doc.body)))
      .catch((err) => this.handleError(err))
  }

  /**
   * It gets patient administrative data.
   * @summary Get patients by id
   * @param body
   */
  async getPatients(body?: ListOfIds): Promise<Array<Patient>> {
    const _url = this.host + `/patient/byIds` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('POST', _url, headers, body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((it) => new Patient(it)))
      .catch((err) => this.handleError(err))
  }

  /**
   * Returns a list of deleted patients, within the specified time period, if any.
   * @summary Find deleted patients
   * @param startDate Filter deletions after this date (unix epoch), included
   * @param endDate Filter deletions before this date (unix epoch), included
   * @param startKey the startKey provided by the previous page or undefined for the first page.
   * @param desc Descending
   * @param startDocumentId A patient document ID
   * @param limit Number of rows
   */
  async listDeletedPatients(
    startDate?: number,
    endDate?: number,
    desc?: boolean,
    startKey?: string,
    startDocumentId?: string,
    limit?: number
  ): Promise<PaginatedListPatient> {
    const _url =
      this.host +
      `/patient/deleted/byDate` +
      '?ts=' +
      new Date().getTime() +
      (startDate ? '&startDate=' + encodeURIComponent(startDate) : '') +
      (endDate ? '&endDate=' + encodeURIComponent(endDate) : '') +
      (desc ? '&desc=' + encodeURIComponent(desc) : '') +
      (startKey ? '&startKey=' + encodeURIComponent(startKey) : '') +
      (startDocumentId ? '&startDocumentId=' + encodeURIComponent(startDocumentId) : '') +
      (limit ? '&limit=' + encodeURIComponent(limit) : '')
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, null, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new PaginatedListPatient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * Returns a list of deleted patients, by name and/or firstname prefix, if any.
   * @summary Find deleted patients
   * @param firstName First name prefix
   * @param lastName Last name prefix
   */
  async listDeletedPatientsByName(firstName?: string, lastName?: string): Promise<Array<Patient>> {
    let _body = null

    const _url =
      this.host +
      `/patient/deleted/by_name` +
      '?ts=' +
      new Date().getTime() +
      (firstName ? '&firstName=' + encodeURIComponent(String(firstName)) : '') +
      (lastName ? '&lastName=' + encodeURIComponent(String(lastName)) : '')
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((it) => new Patient(it)))
      .catch((err) => this.handleError(err))
  }

  /**
   * Returns a list of patients that have been merged after the provided date
   * @summary List patients that have been merged towards another patient
   * @param date
   */
  async listOfMergesAfter(date: number): Promise<Array<Patient>> {
    let _body = null

    const _url = this.host + `/patient/merges/${encodeURIComponent(String(date))}` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((it) => new Patient(it)))
      .catch((err) => this.handleError(err))
  }

  /**
   * Returns a list of patients that have been modified after the provided date
   * @summary List patients that have been modified after the provided date
   * @param date
   * @param startKey The start key for pagination the date of the first element of the new page
   * @param startDocumentId A patient document ID
   * @param limit Number of rows
   */
  async listOfPatientsModifiedAfter(date: number, startKey?: number, startDocumentId?: string, limit?: number): Promise<PaginatedListPatient> {
    let _body = null

    const _url =
      this.host +
      `/patient/modifiedAfter/${encodeURIComponent(String(date))}` +
      '?ts=' +
      new Date().getTime() +
      (startKey ? '&startKey=' + encodeURIComponent(String(startKey)) : '') +
      (startDocumentId ? '&startDocumentId=' + encodeURIComponent(String(startDocumentId)) : '') +
      (limit ? '&limit=' + encodeURIComponent(String(limit)) : '')
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new PaginatedListPatient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * @deprecated This method is not intuitive and will be removed in a future version
   * Currently there is no direct replacement that can do everything this method does.
   * If you need all patients unsorted or sorted by ssin/date of birth use `PatientByHcPartyFilter`/`PatientByHcPartyDateOfBirthFilter`
   * with {@link matchPatientsBy} to get the ids of all the patients shared directly with an HCP (ignoring hierarchies).
   * Note that on databases with many patients (>50k) the matches request could take various seconds as it is not
   * paginated.
   * If you want to use pagination or need to sort by name instead you can use the {@link listPatientsSortedByName}.
   * Usage of PatientByHcPartyNameFilter or PatientByHcPartyNameContainsFuzzyFilter as a replacement of this method is
   * discouraged as the sorting done by those filter is unintuitive.
   * Contact us if your use case is not covered by the suggested alternatives.
   *
   * This method returns all the patients (with pagination) that are shared directly with the parent of {@link hcPartyId},
   * or if undefined the current user's parent. Note that this doesn't allow to get patients that are shared only with
   * the current user and not its parent.
   */
  async listPatients(
    hcPartyId?: string,
    sortField?: string,
    startKey?: string,
    startDocumentId?: string,
    limit?: number,
    sortDirection?: string
  ): Promise<PaginatedListPatient> {
    let _body = null

    const _url =
      this.host +
      `/patient` +
      '?ts=' +
      new Date().getTime() +
      (hcPartyId ? '&hcPartyId=' + encodeURIComponent(String(hcPartyId)) : '') +
      (sortField ? '&sortField=' + encodeURIComponent(String(sortField)) : '') +
      (startKey ? '&startKey=' + encodeURIComponent(String(startKey)) : '') +
      (startDocumentId ? '&startDocumentId=' + encodeURIComponent(String(startDocumentId)) : '') +
      (limit ? '&limit=' + encodeURIComponent(String(limit)) : '') +
      (sortDirection ? '&sortDirection=' + encodeURIComponent(String(sortDirection)) : '')
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new PaginatedListPatient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * Get all patients shared directly with the provided hcparty (ignoring hierarchies) sorted by lastName, firstName.
   */
  async listPatientsSortedByName(hcPartyId?: string, startKey?: string, startDocumentId?: string, limit?: number): Promise<PaginatedListPatient> {
    let _body = null

    const _url =
      this.host +
      `/patient/byHcPartyId` +
      '?ts=' +
      new Date().getTime() +
      (hcPartyId ? '&hcPartyId=' + encodeURIComponent(String(hcPartyId)) : '') +
      (startKey ? '&startKey=' + encodeURIComponent(String(startKey)) : '') +
      (startDocumentId ? '&startDocumentId=' + encodeURIComponent(String(startDocumentId)) : '') +
      (limit ? '&limit=' + encodeURIComponent(String(limit)) : '')
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new PaginatedListPatient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * Returns a list of patients along with next start keys and Document ID. If the nextStartKey is Null it means that this is the last page.
   * @summary List patients for a specific HcParty or for the current HcParty
   * @param hcPartyId
   * @param sortField Optional value for sorting results by a given field (&#x27;name&#x27;, &#x27;ssin&#x27;, &#x27;dateOfBirth&#x27;). Specifying this deactivates filtering
   * @param startKey The start key for pagination: a JSON representation of an array containing all the necessary components to form the Complex Key&#x27;s startKey
   * @param startDocumentId A patient document ID
   * @param limit Number of rows
   * @param sortDirection Optional value for providing a sorting direction (&#x27;asc&#x27;, &#x27;desc&#x27;). Set to &#x27;asc&#x27; by default.
   */
  async listPatientsByHcParty(
    hcPartyId: string,
    sortField?: string,
    startKey?: string,
    startDocumentId?: string,
    limit?: number,
    sortDirection?: string
  ): Promise<PaginatedListPatient> {
    let _body = null

    const _url =
      this.host +
      `/patient/hcParty/${encodeURIComponent(String(hcPartyId))}` +
      '?ts=' +
      new Date().getTime() +
      (sortField ? '&sortField=' + encodeURIComponent(String(sortField)) : '') +
      (startKey ? '&startKey=' + encodeURIComponent(String(startKey)) : '') +
      (startDocumentId ? '&startDocumentId=' + encodeURIComponent(String(startDocumentId)) : '') +
      (limit ? '&limit=' + encodeURIComponent(String(limit)) : '') +
      (sortDirection ? '&sortDirection=' + encodeURIComponent(String(sortDirection)) : '')
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new PaginatedListPatient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * Returns a list of patients along with next start keys and Document ID. If the nextStartKey is Null it means that this is the last page.
   * @summary List patients by pages for a specific HcParty
   * @param hcPartyId Healthcare party id
   * @param startKey The page first id
   * @param startDocumentId A patient document ID
   * @param limit Page size
   */
  async listPatientsIds(hcPartyId: string, startKey?: string, startDocumentId?: string, limit?: number): Promise<PaginatedListString> {
    let _body = null

    const _url =
      this.host +
      `/patient/byHcPartyId` +
      '?ts=' +
      new Date().getTime() +
      (hcPartyId ? '&hcPartyId=' + encodeURIComponent(String(hcPartyId)) : '') +
      (startKey ? '&startKey=' + encodeURIComponent(String(startKey)) : '') +
      (startDocumentId ? '&startDocumentId=' + encodeURIComponent(String(startDocumentId)) : '') +
      (limit ? '&limit=' + encodeURIComponent(String(limit)) : '')
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new PaginatedListString(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * Returns a list of patients along with next start keys and Document ID. If the nextStartKey is Null it means that this is the last page.
   * @summary List patients of a specific HcParty or of the current HcParty
   * @param hcPartyId
   * @param sortField Optional value for sorting results by a given field (&#x27;name&#x27;, &#x27;ssin&#x27;, &#x27;dateOfBirth&#x27;). Specifying this deactivates filtering
   * @param startKey The start key for pagination: a JSON representation of an array containing all the necessary components to form the Complex Key&#x27;s startKey
   * @param startDocumentId A patient document ID
   * @param limit Number of rows
   * @param sortDirection Optional value for providing a sorting direction (&#x27;asc&#x27;, &#x27;desc&#x27;). Set to &#x27;asc&#x27; by default.
   */
  async listPatientsOfHcParty(
    hcPartyId: string,
    sortField?: string,
    startKey?: string,
    startDocumentId?: string,
    limit?: number,
    sortDirection?: string
  ): Promise<PaginatedListPatient> {
    let _body = null

    const _url =
      this.host +
      `/patient/ofHcParty/${encodeURIComponent(String(hcPartyId))}` +
      '?ts=' +
      new Date().getTime() +
      (sortField ? '&sortField=' + encodeURIComponent(String(sortField)) : '') +
      (startKey ? '&startKey=' + encodeURIComponent(String(startKey)) : '') +
      (startDocumentId ? '&startDocumentId=' + encodeURIComponent(String(startDocumentId)) : '') +
      (limit ? '&limit=' + encodeURIComponent(String(limit)) : '') +
      (sortDirection ? '&sortDirection=' + encodeURIComponent(String(sortDirection)) : '')
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new PaginatedListPatient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   *
   * @summary Get ids of patients matching the provided filter for the current user (HcParty)
   * @param body
   * @param collectTiming if true, include server-side filter timing information in the response
   */
  matchPatientsBy(body?: AbstractFilterPatient, collectTiming?: false): Promise<Array<string>>
  matchPatientsBy(body?: AbstractFilterPatient, collectTiming?: true): Promise<Array<string> & TimingInfo>
  async matchPatientsBy(body?: AbstractFilterPatient, collectTiming: boolean = false): Promise<Array<string>> {
    const _url = this.host + `/patient/match` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand(
      'POST',
      _url,
      headers,
      body,
      this.fetchImpl,
      undefined,
      this.authenticationProvider.getAuthService(),
      undefined,
      false,
      collectTiming ? ['x-filter-timing-*'] : []
    )
      .then((doc) =>
        Object.assign(
          (doc.body as Array<JSON>).map((it) => JSON.parse(JSON.stringify(it))),
          collectTiming ? { responseHeaders: doc.responseHeaders } : {}
        )
      )
      .catch((err) => this.handleError(err))
  }

  /**
   *
   * @summary Merge a series of patients into another patient
   * @param toId
   * @param fromIds
   */
  async mergeInto(toId: string, fromIds: string): Promise<Patient> {
    let _body = null

    const _url =
      this.host + `/patient/mergeInto/${encodeURIComponent(String(toId))}/from/${encodeURIComponent(String(fromIds))}` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('PUT', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new Patient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * No particular return value. It's just a message.
   * @summary Modify a patient
   * @param body
   */
  async modifyPatient(body?: Patient): Promise<Patient> {
    let _body = null
    _body = body

    const _url = this.host + `/patient` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('PUT', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new Patient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   *
   * @summary Set a patient referral doctor
   * @param patientId
   * @param referralId The referal id. Accepts &#x27;none&#x27; for referral removal.
   * @param start Optional value for start of referral
   * @param end Optional value for end of referral
   */
  async modifyPatientReferral(patientId: string, referralId: string, start?: number, end?: number): Promise<Patient> {
    let _body = null

    const _url =
      this.host +
      `/patient/${encodeURIComponent(String(patientId))}/referral/${encodeURIComponent(String(referralId))}` +
      '?ts=' +
      new Date().getTime() +
      (start ? '&start=' + encodeURIComponent(String(start)) : '') +
      (end ? '&end=' + encodeURIComponent(String(end)) : '')
    let headers = await this.headers
    return XHR.sendCommand('PUT', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new Patient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * Register a new patient into the system
   * @summary Register a patient
   * @param body
   * @param hcPartyId
   * @param groupId
   * @param token
   * @param useShortToken
   */
  async registerPatient(
    hcPartyId: string,
    groupId: string,
    token?: string,
    useShortToken?: boolean,
    body?: Patient
  ): Promise<DataOwnerRegistrationSuccess> {
    let _body = null
    _body = body

    const _url =
      this.host +
      `/patient/register/forHcp/${encodeURIComponent(String(hcPartyId))}/inGroup/${encodeURIComponent(String(groupId))}` +
      '?ts=' +
      new Date().getTime() +
      (token ? '&token=' + encodeURIComponent(String(token)) : '') +
      (useShortToken ? '&useShortToken=' + encodeURIComponent(String(useShortToken)) : '')
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('POST', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new DataOwnerRegistrationSuccess(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * Response is an array containing the ID of undeleted patient..
   * @summary undelete previously deleted patients
   * @param patientIds
   */
  async undeletePatient(patientIds: string): Promise<Array<DocIdentifier>> {
    let _body = null

    const _url = this.host + `/patient/undelete/${encodeURIComponent(String(patientIds))}` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('PUT', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((it) => new DocIdentifier(it)))
      .catch((err) => this.handleError(err))
  }

  /**
   * @internal this method is for internal use only and may be changed without notice
   */
  async bulkSharePatients(request: BulkShareOrUpdateMetadataParams): Promise<EntityBulkShareResult<Patient>[]> {
    const _url = this.host + '/patient/bulkSharedMetadataUpdate' + '?ts=' + new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('PUT', _url, headers, request, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((x) => new EntityBulkShareResult<Patient>(x, Patient)))
      .catch((err) => this.handleError(err))
  }

  /**
   * @internal use mergePatients from the extended api instead
   */
  async baseMergePatients(fromId: string, expectedFromRev: string, updatedInto: Patient, omitEncryptionKeysOfFrom: boolean) {
    let _body = updatedInto

    const _url =
      this.host +
      `/patient/mergeInto/${encodeURIComponent(String(updatedInto.id))}/from/${encodeURIComponent(String(fromId))}` +
      '?ts=' +
      new Date().getTime() +
      '&expectedFromRev=' +
      encodeURIComponent(String(expectedFromRev)) +
      '&omitEncryptionKeysOfFrom=' +
      encodeURIComponent(omitEncryptionKeysOfFrom)
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('PUT', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new Patient(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * Retrieves the ids of all the patients that currently have conflicting revisions and therefore need to
   * be resolved.
   * @summary List the ids of the patients that have conflicts.
   * @return the ids of the patients with unresolved conflicts.
   */
  async getConflictingEntitiesIds(): Promise<Array<string>> {
    const _url = this.host + `/patient/conflicts` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, null, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => doc.body as Array<string>)
      .catch((err) => this.handleError(err))
  }

  /**
   * Retrieves all the conflicting revisions of the patient with the given id (the current revision is not
   * included). The returned entities are still encrypted.
   * @summary Get the conflicting revisions of a patient.
   * @param entityId the id of the patient to retrieve the conflicts for.
   * @return the conflicting revisions of the patient.
   */
  async getConflictsForEntity(entityId: string): Promise<Array<Patient>> {
    const _url = this.host + `/patient/conflicts/of?entityId=${encodeURIComponent(String(entityId))}` + '&ts=' + new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, null, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((it) => new Patient(it)))
      .catch((err) => this.handleError(err))
  }

  /**
   * Resolves a conflict by declaring which revision of the patient should be kept as winner and which
   * conflicting revisions should be purged. The provided document must already be encrypted.
   * @summary Declare the winning revision of a conflicting patient.
   * @param body the {@link ConflictResolutionRequest} carrying the winning revision and the conflicts to purge.
   * @return the {@link ConflictResolutionResult} with the saved winner and the conflicts that are still unresolved.
   */
  async declareConflictWinner(body: ConflictResolutionRequest<Patient>): Promise<ConflictResolutionResult<Patient>> {
    const _url = this.host + `/patient/conflicts/winner` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('POST', _url, headers, body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new ConflictResolutionResult<Patient>(doc.body, (x) => new Patient(x)))
      .catch((err) => this.handleError(err))
  }

  /**
   * Like {@link getConflictingEntitiesIds} but targets the entities of the group with the given id.
   * @summary List the ids of the patients that have conflicts, in the given group.
   * @param groupId the id of the group to look into.
   * @return the ids of the patients with unresolved conflicts in the group.
   */
  async getConflictingEntitiesIdsInGroup(groupId: string): Promise<Array<string>> {
    const _url = this.host + `/patient/inGroup/${encodeURIComponent(String(groupId))}/conflicts` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, null, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => doc.body as Array<string>)
      .catch((err) => this.handleError(err))
  }

  /**
   * Like {@link getConflictsForEntity} but targets the entity of the group with the given id.
   * @summary Get the conflicting revisions of a patient, in the given group.
   * @param groupId the id of the group the patient belongs to.
   * @param entityId the id of the patient to retrieve the conflicts for.
   * @return the conflicting revisions of the patient.
   */
  async getConflictsForEntityInGroup(groupId: string, entityId: string): Promise<Array<Patient>> {
    const _url =
      this.host +
      `/patient/inGroup/${encodeURIComponent(String(groupId))}/conflicts/of?entityId=${encodeURIComponent(String(entityId))}` +
      '&ts=' +
      new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, null, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((it) => new Patient(it)))
      .catch((err) => this.handleError(err))
  }

  /**
   * Like {@link declareConflictWinner} but targets the entity of the group with the given id.
   * @summary Declare the winning revision of a conflicting patient, in the given group.
   * @param groupId the id of the group the patient belongs to.
   * @param body the {@link ConflictResolutionRequest} carrying the winning revision and the conflicts to purge.
   * @return the {@link ConflictResolutionResult} with the saved winner and the conflicts that are still unresolved.
   */
  async declareConflictWinnerInGroup(groupId: string, body: ConflictResolutionRequest<Patient>): Promise<ConflictResolutionResult<Patient>> {
    const _url = this.host + `/patient/inGroup/${encodeURIComponent(String(groupId))}/conflicts/winner` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('POST', _url, headers, body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new ConflictResolutionResult<Patient>(doc.body, (x) => new Patient(x)))
      .catch((err) => this.handleError(err))
  }

  /**
   * Automatically resolves the conflicting revisions of the patients with the given ids, according to the provided
   * {@link ConflictResolutionStrategy}.
   * @summary Automatically solve the conflicts of the patients with the given ids.
   * @param entityIds the ids of the patients to solve the conflicts for.
   * @param strategy the {@link ConflictResolutionStrategy} to use to resolve the conflicts. Defaults to `FullMergeability`.
   * @return the {@link MergeResult} of the conflict resolution for each requested entity.
   */
  async autoSolveConflicts(
    entityIds: Array<string>,
    strategy: ConflictResolutionStrategy = ConflictResolutionStrategy.FullMergeability
  ): Promise<Array<MergeResult>> {
    const _url = this.host + `/patient/conflicts/solve?strategy=${encodeURIComponent(String(strategy))}` + '&ts=' + new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('POST', _url, headers, entityIds, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((it) => new MergeResult(it)))
      .catch((err) => this.handleError(err))
  }

  /**
   * Like {@link autoSolveConflicts} but targets the entities of the group with the given id.
   * @summary Automatically solve the conflicts of the patients with the given ids, in the given group.
   * @param groupId the id of the group to look into.
   * @param entityIds the ids of the patients to solve the conflicts for.
   * @param strategy the {@link ConflictResolutionStrategy} to use to resolve the conflicts. Defaults to `FullMergeability`.
   * @return the {@link MergeResult} of the conflict resolution for each requested entity.
   */
  async autoSolveConflictsInGroup(
    groupId: string,
    entityIds: Array<string>,
    strategy: ConflictResolutionStrategy = ConflictResolutionStrategy.FullMergeability
  ): Promise<Array<MergeResult>> {
    const _url =
      this.host +
      `/patient/inGroup/${encodeURIComponent(String(groupId))}/conflicts/solve?strategy=${encodeURIComponent(String(strategy))}` +
      '&ts=' +
      new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('POST', _url, headers, entityIds, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((it) => new MergeResult(it)))
      .catch((err) => this.handleError(err))
  }
}
